"use client";

import RTHMDirectLogo from "@/components/SVG/Logos/RTHM";
import { SidebarToggle } from "@/components/sidebar-toggle";
import { Button } from "@/components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import {
    Bars3Icon,
    BeakerIcon,
    ChartBarSquareIcon,
    ChatBubbleLeftRightIcon,
    EllipsisHorizontalIcon,
    // Added for mobile "More"
    PowerIcon,
    QuestionMarkCircleIcon,
    UserIcon,
} from "@heroicons/react/24/outline";
import {
    Calendar,
    ChevronDownIcon,
    Database,
    HomeIcon,
    MoonIcon,
    PlusIcon,
    SunIcon,
} from "lucide-react";
import { Session } from "next-auth";
import { signOut } from "next-auth/react";
import { useTheme } from "next-themes";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { usePostHog } from "posthog-js/react";
import React, { useEffect, useRef, useState } from "react";

interface IntelligenceHeaderClientProps {
    session: Session | null;
}

export default function IntelligenceHeaderClient({
    session,
}: IntelligenceHeaderClientProps) {
    const isMobile = useIsMobile();
    const router = useRouter();
    const posthog = usePostHog();
    const [mounted, setMounted] = useState(false);
    const { theme, setTheme } = useTheme();
    const pathname = usePathname();

    // Intelligence refs
    const dashboardRef = useRef<HTMLAnchorElement>(null);
    const chatRef = useRef<HTMLAnchorElement>(null);
    const myDataRef = useRef<HTMLAnchorElement>(null);
    const treatmentsRef = useRef<HTMLAnchorElement>(null);
    const homeRef = useRef<HTMLAnchorElement>(null);

    // Direct refs
    const directDashboardRef = useRef<HTMLAnchorElement>(null);
    const directTreatmentsRef = useRef<HTMLAnchorElement>(null);
    const directAppointmentsRef = useRef<HTMLAnchorElement>(null);
    const processRef = useRef<HTMLAnchorElement>(null);
    const intelligenceFromDirectRef = useRef<HTMLAnchorElement>(null);
    const loginRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        setMounted(true);
    }, []);

    const logOut = async () => {
        posthog?.capture("sign_out");
        posthog?.reset();
        document.cookie =
            "visitorId-discover=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
        document.cookie =
            "visitorId-intelligence=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
        localStorage.removeItem("visitorId-discover");
        localStorage.removeItem("visitorId-intelligence");
        localStorage.removeItem("healthBannerCompletedDismissed");
        await signOut({ redirectTo: "/", redirect: true });
    };

    const isActive = (path: string) => {
        if (path === "/") {
            return pathname === "/" || pathname === "/home";
        }
        return pathname.startsWith(path);
    };

    const renderNavigationLinks = () => {
        // Helper to generate icon class names for mobile
        const getMobileIconClasses = (path: string) =>
            cn(
                "h-5 w-5 mb-0.5",
                isActive(path) && isMobile
                    ? "text-neutral-800 dark:text-neutral-100"
                    : "text-neutral-500 dark:text-neutral-400",
            );
        // Helper to generate text class names
        const getMobileTextClasses = (path: string) =>
            cn(
                "text-xs",
                isActive(path) && isMobile
                    ? "text-neutral-800 dark:text-neutral-100 font-medium"
                    : "text-neutral-500 dark:text-neutral-400",
            );
        const getDesktopTextClasses = (path: string) =>
            cn(
                "text-sm",
                isActive(path)
                    ? "text-black dark:text-white font-semibold"
                    : "text-neutral-700 dark:text-neutral-300 hover:text-black dark:hover:text-white",
            );

        const getLinkClasses = (path: string) =>
            cn(
                "relative transition-colors z-10",
                isMobile
                    ? "flex flex-col items-center justify-center p-2 rounded-md"
                    : "px-3 py-1.5 rounded-full",
                isActive(path) && !isMobile
                    ? "bg-neutral-300 dark:bg-neutral-700"
                    : "",
                isActive(path) && isMobile
                    ? "bg-neutral-200 dark:bg-neutral-700"
                    : "",
            );

        if (pathname.includes("/intelligence")) {
            return (
                <>
                    {session?.user && (
                        <>
                            <Link
                                ref={dashboardRef}
                                data-tour-id="header-dashboard-link"
                                href="/intelligence/dashboard"
                                onClick={() =>
                                    posthog?.capture(
                                        "intelligence_dashboard_from_header",
                                    )
                                }
                                className={getLinkClasses(
                                    "/intelligence/dashboard",
                                )}
                            >
                                {isMobile && (
                                    <HomeIcon
                                        className={getMobileIconClasses(
                                            "/intelligence/dashboard",
                                        )}
                                    />
                                )}
                                <span
                                    className={
                                        isMobile
                                            ? getMobileTextClasses(
                                                  "/intelligence/dashboard",
                                              )
                                            : getDesktopTextClasses(
                                                  "/intelligence/dashboard",
                                              )
                                    }
                                >
                                    Dashboard
                                </span>
                            </Link>
                            <Link
                                ref={chatRef}
                                data-tour-id="header-ai-chat-link"
                                href="/intelligence/chat"
                                onClick={() =>
                                    posthog?.capture(
                                        "intelligence_ai_chat_page_from_header",
                                    )
                                }
                                className={getLinkClasses("/intelligence/chat")}
                            >
                                {isMobile && (
                                    <ChatBubbleLeftRightIcon
                                        className={getMobileIconClasses(
                                            "/intelligence/chat",
                                        )}
                                    />
                                )}
                                <span
                                    className={
                                        isMobile
                                            ? getMobileTextClasses(
                                                  "/intelligence/chat",
                                              )
                                            : getDesktopTextClasses(
                                                  "/intelligence/chat",
                                              )
                                    }
                                >
                                    AI Chat
                                </span>
                            </Link>
                            <Link
                                ref={myDataRef}
                                data-tour-id="header-my-data-link"
                                href="/intelligence/my-data"
                                onClick={() =>
                                    posthog?.capture(
                                        "intelligence_my_data_from_header",
                                    )
                                }
                                className={getLinkClasses(
                                    "/intelligence/my-data",
                                )}
                            >
                                {isMobile && (
                                    <Database
                                        className={getMobileIconClasses(
                                            "/intelligence/my-data",
                                        )}
                                    />
                                )}
                                <span
                                    className={
                                        isMobile
                                            ? getMobileTextClasses(
                                                  "/intelligence/my-data",
                                              )
                                            : getDesktopTextClasses(
                                                  "/intelligence/my-data",
                                              )
                                    }
                                >
                                    My Data
                                </span>
                            </Link>
                            <Link
                                ref={treatmentsRef}
                                data-tour-id="header-treatments-link"
                                href="/intelligence/treatments"
                                onClick={() =>
                                    posthog?.capture(
                                        "intelligence_treatment_suggestions_from_header",
                                    )
                                }
                                className={getLinkClasses(
                                    "/intelligence/treatments",
                                )}
                            >
                                {isMobile && (
                                    <BeakerIcon
                                        className={getMobileIconClasses(
                                            "/intelligence/treatments",
                                        )}
                                    />
                                )}
                                <span
                                    className={
                                        isMobile
                                            ? getMobileTextClasses(
                                                  "/intelligence/treatments",
                                              )
                                            : getDesktopTextClasses(
                                                  "/intelligence/treatments",
                                              )
                                    }
                                >
                                    Treatments
                                </span>
                            </Link>
                        </>
                    )}
                    {!session?.user && (
                        <Link
                            ref={homeRef}
                            href="/"
                            onClick={() =>
                                posthog?.capture(
                                    "home_page_from_intelligence_header",
                                )
                            }
                            className={getLinkClasses("/")}
                        >
                            {isMobile && (
                                <RTHMDirectLogo
                                    className={cn(
                                        "h-5 w-auto mb-0.5",
                                        isActive("/") && isMobile
                                            ? "fill-neutral-800 dark:fill-neutral-100"
                                            : "fill-neutral-500 dark:fill-neutral-400",
                                    )}
                                />
                            )}
                            <span
                                className={
                                    isMobile
                                        ? getMobileTextClasses("/")
                                        : getDesktopTextClasses("/")
                                }
                            >
                                RTHM Direct
                            </span>
                        </Link>
                    )}
                </>
            );
        } else {
            return (
                <>
                    {session?.user ? (
                        <>
                            <Link
                                ref={directDashboardRef}
                                href="/dashboard"
                                onClick={() =>
                                    posthog?.capture(
                                        "direct_page_from_intelligence_header",
                                    )
                                }
                                className={getLinkClasses("/dashboard")}
                            >
                                {isMobile && (
                                    <HomeIcon
                                        className={getMobileIconClasses(
                                            "/dashboard",
                                        )}
                                    />
                                )}
                                <span
                                    className={
                                        isMobile
                                            ? getMobileTextClasses("/dashboard")
                                            : getDesktopTextClasses(
                                                  "/dashboard",
                                              )
                                    }
                                >
                                    Dashboard
                                </span>
                            </Link>
                            <Link
                                ref={directTreatmentsRef}
                                href="/treatments"
                                onClick={() =>
                                    posthog?.capture(
                                        "treatments_page_from_header",
                                    )
                                }
                                className={getLinkClasses("/treatments")}
                            >
                                {isMobile && (
                                    <BeakerIcon
                                        className={getMobileIconClasses(
                                            "/treatments",
                                        )}
                                    />
                                )}
                                <span
                                    className={
                                        isMobile
                                            ? getMobileTextClasses(
                                                  "/treatments",
                                              )
                                            : getDesktopTextClasses(
                                                  "/treatments",
                                              )
                                    }
                                >
                                    Treatments
                                </span>
                            </Link>
                            <Link
                                ref={directAppointmentsRef}
                                href="/appointments"
                                onClick={() =>
                                    posthog?.capture(
                                        "appointments_page_from_header",
                                    )
                                }
                                className={getLinkClasses("/appointments")}
                            >
                                {isMobile && (
                                    <Calendar
                                        className={getMobileIconClasses(
                                            "/appointments",
                                        )}
                                    />
                                )}
                                <span
                                    className={
                                        isMobile
                                            ? getMobileTextClasses(
                                                  "/appointments",
                                              )
                                            : getDesktopTextClasses(
                                                  "/appointments",
                                              )
                                    }
                                >
                                    Appointments
                                </span>
                            </Link>
                        </>
                    ) : (
                        <>
                            <Link
                                ref={directTreatmentsRef}
                                href="/treatments"
                                onClick={() =>
                                    posthog?.capture(
                                        "treatments_page_from_header",
                                    )
                                }
                                className={getLinkClasses("/treatments")}
                            >
                                {isMobile && (
                                    <BeakerIcon
                                        className={getMobileIconClasses(
                                            "/treatments",
                                        )}
                                    />
                                )}
                                <span
                                    className={
                                        isMobile
                                            ? getMobileTextClasses(
                                                  "/treatments",
                                              )
                                            : getDesktopTextClasses(
                                                  "/treatments",
                                              )
                                    }
                                >
                                    Treatments
                                </span>
                            </Link>
                            <Link
                                ref={processRef}
                                href="/how-it-works"
                                onClick={() =>
                                    posthog?.capture("process_page_from_header")
                                }
                                className={getLinkClasses("/how-it-works")}
                            >
                                {isMobile && (
                                    <QuestionMarkCircleIcon
                                        className={getMobileIconClasses(
                                            "/how-it-works",
                                        )}
                                    />
                                )}
                                <span
                                    className={
                                        isMobile
                                            ? getMobileTextClasses(
                                                  "/how-it-works",
                                              )
                                            : getDesktopTextClasses(
                                                  "/how-it-works",
                                              )
                                    }
                                >
                                    Process
                                </span>
                            </Link>
                            <Link
                                ref={intelligenceFromDirectRef}
                                href="/intelligence"
                                onClick={() =>
                                    posthog?.capture(
                                        "intelligence_page_from_header",
                                    )
                                }
                                className={getLinkClasses("/intelligence")}
                            >
                                {isMobile && (
                                    <ChartBarSquareIcon
                                        className={getMobileIconClasses(
                                            "/intelligence",
                                        )}
                                    />
                                )}
                                <span
                                    className={
                                        isMobile
                                            ? getMobileTextClasses(
                                                  "/intelligence",
                                              )
                                            : getDesktopTextClasses(
                                                  "/intelligence",
                                              )
                                    }
                                >
                                    Intelligence
                                </span>
                            </Link>
                        </>
                    )}
                </>
            );
        }
    };

    const renderProfileMenu = () => (
        <>
            {session?.user ? (
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button
                            variant="ghost"
                            data-tour-id="header-more-button"
                            className={cn(
                                "relative z-10 focus:outline-none focus-visible:ring-0 focus:ring-0",
                                isMobile
                                    ? "p-2 flex flex-col items-center justify-center rounded-md" // Mobile "More" icon button
                                    : "text-sm px-3 py-1.5 flex items-center space-x-1 text-neutral-700 dark:text-neutral-400 hover:text-black dark:hover:text-white transition-colors", // Desktop "More" text button
                            )}
                            data-no-bubble="true"
                        >
                            {isMobile ? (
                                <>
                                    <EllipsisHorizontalIcon className="h-5 w-5 text-neutral-500 dark:text-neutral-400" />
                                    <span className="text-xs text-neutral-500 dark:text-neutral-400">
                                        More
                                    </span>
                                </>
                            ) : (
                                <>
                                    <span>More</span>
                                    <ChevronDownIcon className="h-4 w-4 opacity-70" />
                                </>
                            )}
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                        align="end"
                        className="w-56 mt-1 border border-border/80 shadow-lg rounded-lg overflow-hidden"
                    >
                        <DropdownMenuItem
                            onClick={() =>
                                setTheme(theme === "dark" ? "light" : "dark")
                            }
                        >
                            {mounted && theme === "dark" ? (
                                <div className="flex items-center">
                                    <SunIcon className="w-5 h-5 mr-6 text-yellow-400" />{" "}
                                    Light Mode
                                </div>
                            ) : (
                                <div className="flex items-center">
                                    <MoonIcon className="w-5 h-5 mr-6 text-foreground/80" />{" "}
                                    Dark Mode
                                </div>
                            )}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        {session.user.canAccessDirect &&
                            pathname !== "/dashboard" && (
                                <DropdownMenuItem asChild>
                                    <Link
                                        href="/dashboard"
                                        onClick={() =>
                                            posthog?.capture(
                                                "dashboard_page_from_header",
                                            )
                                        }
                                        className="flex items-center cursor-pointer"
                                    >
                                        <ChartBarSquareIcon className="w-5 h-5 mr-4 text-foreground/80" />{" "}
                                        Direct Dashboard
                                    </Link>
                                </DropdownMenuItem>
                            )}
                        {!pathname.startsWith("/intelligence") && (
                            <DropdownMenuItem asChild>
                                <Link
                                    href="/intelligence/dashboard"
                                    onClick={() =>
                                        posthog?.capture(
                                            "intelligence_dashboard_page_from_header",
                                        )
                                    }
                                    className="flex items-center cursor-pointer"
                                >
                                    <Bars3Icon className="w-5 h-5 mr-4 text-foreground/80" />{" "}
                                    Intelligence Dashboard
                                </Link>
                            </DropdownMenuItem>
                        )}
                        <DropdownMenuItem asChild>
                            <Link
                                href="/profile"
                                onClick={() =>
                                    posthog?.capture("profile_page_from_header")
                                }
                                className="flex items-center cursor-pointer"
                            >
                                <UserIcon className="w-5 h-5 mr-4 text-foreground/80" />{" "}
                                Profile
                            </Link>
                        </DropdownMenuItem>
                        {pathname.includes("/intelligence") ? (
                            <DropdownMenuItem asChild>
                                <Link
                                    href="/intelligence/support"
                                    onClick={() =>
                                        posthog?.capture(
                                            "support_page_from_header",
                                        )
                                    }
                                    className="flex items-center cursor-pointer"
                                >
                                    <QuestionMarkCircleIcon className="w-5 h-5 mr-4 text-foreground/80" />{" "}
                                    Support
                                </Link>
                            </DropdownMenuItem>
                        ) : (
                            <DropdownMenuItem asChild>
                                <Link
                                    href="/support"
                                    onClick={() =>
                                        posthog?.capture(
                                            "support_page_from_header",
                                        )
                                    }
                                    className="flex items-center cursor-pointer"
                                >
                                    <QuestionMarkCircleIcon className="w-5 h-5 mr-4 text-foreground/80" />{" "}
                                    Support
                                </Link>
                            </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                            onClick={logOut}
                            className="cursor-pointer"
                        >
                            <PowerIcon className="w-5 h-5 mr-4 text-foreground/80" />{" "}
                            Sign Out
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            ) : (
                <Button
                    ref={loginRef}
                    id="log-in-from-header"
                    variant="ghost"
                    size="sm"
                    className={cn(
                        "px-3 py-1 rounded-full transition-colors flex flex-col items-center z-10 h-auto",
                        isActive("/sign-in") ? "text-primary font-medium" : "",
                        isMobile ? "" : "mx-2",
                    )}
                    onClick={() => {
                        posthog?.capture("log_in_page_from_header");
                        router.push(`/sign-in?redirectTo=${pathname}`);
                    }}
                >
                    <UserIcon className="h-5 w-5" />
                    <span
                        className={cn(
                            "text-xs mt-1",
                            isMobile ? "" : "hidden md:inline",
                        )}
                    >
                        Log In
                    </span>
                </Button>
            )}
        </>
    );

    if (isMobile) {
        return (
            <>
                {pathname.startsWith("/intelligence/chat") && (
                    <div className="fixed top-4 left-4 z-50">
                        <div data-tour-id="sidebar-toggle-wrapper-desktop">
                            <SidebarToggle />
                        </div>
                    </div>
                )}
                <div className="fixed bottom-0 left-0 right-0 z-50 bg-popover shadow-lg py-2 px-1 border-t ">
                    <div className="flex justify-around items-center relative">
                        {renderNavigationLinks()}
                        {renderProfileMenu()}
                    </div>
                </div>
            </>
        );
    }

    return (
        <header className="flex sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-border/60">
            <div className="w-full mx-auto px-4 lg:px-6 xl:px-8 py-2.5 flex items-center justify-between">
                <div className="flex items-center space-x-4">
                    {pathname.startsWith("/intelligence/chat") && (
                        <div data-tour-id="sidebar-toggle-wrapper-desktop">
                            <SidebarToggle />
                        </div>
                    )}
                    <Link href="/" aria-label="Home">
                        <RTHMDirectLogo className="h-6 w-auto" />
                    </Link>
                </div>
                <div className="flex-1 flex justify-center">
                    <nav className="bg-neutral-200 dark:bg-neutral-800 rounded-full px-2 py-1.5 flex items-center space-x-1 shadow-md relative">
                        {renderNavigationLinks()}
                    </nav>
                </div>
                <div className="flex items-center space-x-3">
                    {renderProfileMenu()}
                </div>
            </div>
        </header>
    );
}
